import "@/App.css";
import { useEffect } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ClickCameraEffect from "@/components/ClickCameraEffect";
import AuroraBackground from "@/components/MovingCameraBackground";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SubmissionCenter from "@/pages/SubmissionCenter";
import SubmitItems from "@/pages/SubmitItems";
import Checkout from "@/pages/Checkout";
import AdminConsole from "@/pages/AdminConsole";
import MyAccount from "@/pages/MyAccount";

function PrivateRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-12 font-mono-rgu text-sm">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function VisitTracker() {
  useEffect(() => {
    try {
      let vid = localStorage.getItem("rgu_visitor_id");
      if (!vid) {
        vid = (window.crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem("rgu_visitor_id", vid);
      }
      // Throttle: max one ping per 30 minutes per browser
      const last = parseInt(localStorage.getItem("rgu_visit_last") || "0", 10);
      const now = Date.now();
      if (now - last < 30 * 60 * 1000) return;
      localStorage.setItem("rgu_visit_last", String(now));
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/visits`, {
        visitor_id: vid,
        path: window.location.pathname || "/",
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  }, []);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors />
        <AuroraBackground />
        <ClickCameraEffect />
        <VisitTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/submit/:authType" element={<PrivateRoute><SubmitItems /></PrivateRoute>} />
          <Route path="/submissions" element={<PrivateRoute><SubmissionCenter /></PrivateRoute>} />
          <Route path="/account" element={<PrivateRoute><MyAccount /></PrivateRoute>} />
          <Route path="/checkout/:submissionId" element={<PrivateRoute><Checkout /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute adminOnly><AdminConsole /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
